// /faq — SSR head() with per-locale metadata and FAQPage JSON-LD.
import { createFileRoute } from "@tanstack/react-router";
import FAQ from "@/pages/FAQ";
import { buildStaticHead, staticLoader, type StaticSEO } from "@/lib/static-route-seo";
import es from "@/i18n/locales/es.json";
import ptBR from "@/i18n/locales/pt-BR.json";
import en from "@/i18n/locales/en.json";
import type { LocaleKey } from "@/lib/locale-from-host";

const SEO: StaticSEO = {
  es: {
    title: "Preguntas Frecuentes (FAQ) | Fora da Rota",
    description:
      "Respuestas a las preguntas más frecuentes sobre reservas, pagos, cancelaciones y viajes a Brasil con Fora da Rota.",
  },
  "pt-BR": {
    title: "Perguntas Frequentes (FAQ) | Fora da Rota",
    description:
      "Respostas às perguntas mais frequentes sobre reservas, pagamentos, cancelamentos e viagens ao Brasil com a Fora da Rota.",
  },
  en: {
    title: "Frequently Asked Questions (FAQ) | Fora da Rota",
    description:
      "Answers to the most common questions about bookings, payments, cancellations and trips to Brazil with Fora da Rota.",
  },
  "en-EU": {
    title: "Frequently Asked Questions (FAQ) | Fora da Rota",
    description:
      "Answers to the most common questions about bookings, payments, cancellations and trips to Brazil with Fora da Rota.",
  },
};

// Mirrors the section/item structure rendered in src/pages/FAQ.tsx.
const SECTIONS: { key: string; itemKeys: string[] }[] = [
  { key: "content", itemKeys: ["howKnow", "updated", "problem", "notFoundDest", "offers"] },
  {
    key: "reservations",
    itemKeys: [
      "howReserve",
      "whyCheaper",
      "hotelGood",
      "doubts",
      "instantConfirm",
      "childDiscount",
      "groupDiscount",
      "minor",
    ],
  },
  {
    key: "payments",
    itemKeys: ["methods", "installments", "fees", "cardDeclined", "confirmed", "invoice"],
  },
  { key: "cancellations", itemKeys: ["policy", "howCancel", "extraFee"] },
  { key: "support", itemKeys: ["noEmail", "duringTrip", "documents"] },
  { key: "about", itemKeys: ["whoWeAre", "safe", "physicalStore"] },
];

const LOCALE_BUNDLES: Record<LocaleKey, Record<string, unknown>> = {
  es: es as Record<string, unknown>,
  "pt-BR": ptBR as Record<string, unknown>,
  en: en as Record<string, unknown>,
  "en-EU": en as Record<string, unknown>,
};

function buildFaqJsonLd(url: string, locale: LocaleKey) {
  const bundle = LOCALE_BUNDLES[locale] ?? LOCALE_BUNDLES.es;
  const faq = ((bundle as { faq?: { sections?: Record<string, Record<string, { q?: string; a?: string }>> } }).faq
    ?.sections) ?? {};
  const mainEntity: Array<Record<string, unknown>> = [];
  for (const section of SECTIONS) {
    const sec = faq[section.key];
    if (!sec) continue;
    for (const itemKey of section.itemKeys) {
      const item = sec[itemKey];
      if (!item?.q || !item?.a) continue;
      mainEntity.push({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      });
    }
  }
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    url,
    mainEntity,
  };
}

export const Route = createFileRoute("/faq")({
  loader: staticLoader,
  head: ({ loaderData }) =>
    buildStaticHead(SEO, "/faq", loaderData, {
      jsonLd: (url) => buildFaqJsonLd(url, loaderData?.locale ?? "es"),
    }),
  component: FAQ,
});
